import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { environment } from '@environments/environment'
import { map, Observable, tap } from 'rxjs'
import { AppService } from '@app/v2/shared/services/app.service'
import { UploadFile } from '../types/upload.type'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { IsImagePipe } from '../pipes/is-image.pipe'
import { FileResponse } from '../types/upload.type'
import { FormGroup, FormArray } from '@angular/forms'
import { Dimension } from '../types/upload-dimensions.type'
import { ApiPayload } from '../types/upload.type'

@Injectable()
export class UploadService {
  constructor(
    private _Http: HttpClient,
    private _IsImagePipe: IsImagePipe,
    private _AppService: AppService
  ) {}

  uploadFile(data: UploadFile): Observable<number | Response<{ uploads: FileResponse }>> {
    const formData = this._AppService.objectToFormData(data)
    return this._Http
      .post<number | Response<{ uploads: FileResponse }>>(`${environment.baseContactlessApi}v3/generic/uploads`, formData, {
        reportProgress: true,
        observe: 'events'
      })
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            const progress = Math.round((100 * event.loaded) / (event.total || 1))
            return progress
          } else if (event instanceof HttpResponse) {
            const response: Response<{ uploads: FileResponse }> = event.body as Response<{ uploads: FileResponse }>
            return response
          }
          return 0
        })
      )
  }

  getFile(fileName: string): Observable<HttpResponse<Blob>> {
    return this._Http.get(`${environment.baseContactlessApi}v3/generic/uploads/${fileName}`, {
      observe: 'response',
      responseType: 'blob'
    })
  }

  deleteFile(fileName: string): Observable<Response<[]>> {
    return this._Http.delete<Response<[]>>(`${environment.baseContactlessApi}v3/generic/uploads/${fileName}`)
  }

  /**
   * @description Check on file size validity
   * @param file File
   * @param maxFileSize number
   * @param minFileSize number
   * @returns string
   */
  public checkFileSizeValidation(file: File, maxFileSize: number, minFileSize: number): string {
    return file.size / 1024 > maxFileSize || file.size / 1024 < minFileSize
      ? `The file size should be (${this._formatFileSize(minFileSize)} ~ ${this._formatFileSize(maxFileSize)})`
      : ''
  }

  /**
   * @description Check on supported file types
   * @param file File
   * @param accept string
   * @returns string
   */
  public checkSupportedTypeValidation(file: File, accept: string): string {
    return accept && !accept?.split(', ')?.some((type) => file.name.endsWith(type) || file.type === type) ? `You can only upload files of type: ${accept?.replace(/\./g, '')}` : ''
  }

  /**
   * @description Check on supported file types
   * @param fileList UploadedFileList
   * @param files File[]
   * @param maxFilesToUpload string
   * @returns string
   */
  public checkFilesLimit(fileList: FormGroup | FormArray, files: File[], maxFilesToUpload: number): string {
    return fileList instanceof FormGroup && files.length > 1
      ? 'Only one file is allowed'
      : fileList instanceof FormArray && files.length > maxFilesToUpload - fileList.controls.length
        ? `No more than ${maxFilesToUpload} files are allowed`
        : ''
  }

  /**
   * @description Validate on image dimension and set value
   * @param img HTMLImageElement
   * @param uploadedImageDimensions Dimension
   * @returns string
   */
  public checkImageDimensionsValidation(img: HTMLImageElement, uploadedImageDimensions: Dimension): string {
    return (uploadedImageDimensions?.minHeight && img.height < uploadedImageDimensions.minHeight) ||
      (uploadedImageDimensions?.maxHeight && img.height > uploadedImageDimensions.maxHeight)
      ? `Uploaded file height should be more than ${uploadedImageDimensions.minHeight}px${
          uploadedImageDimensions.maxHeight ? ` and less than ${uploadedImageDimensions.maxHeight}px` : ''
        }.`
      : (uploadedImageDimensions?.minWidth && img.width < uploadedImageDimensions.minWidth) || (uploadedImageDimensions?.maxWidth && img.width > uploadedImageDimensions.maxWidth)
        ? `Uploaded file width should be more than ${uploadedImageDimensions.minWidth}px${
            uploadedImageDimensions.maxWidth ? ` and less than ${uploadedImageDimensions.maxWidth}px` : ''
          }.`
        : ''
  }

  /**
   * @description Display file size in a readable format
   * @param size number
   * @returns string
   */
  private _formatFileSize(size: number): string {
    return size >= 1024 ? `${(size / 1024).toFixed(2)} MB` : `${size.toFixed(0)} KB`
  }

  /**
   * @description Check if image can't be cropped
   * @param file File
   * @returns boolean
   */
  public isUnCroppedImage(file: File): boolean {
    return ['.svg', '.gif'].some((type) => file.type.endsWith(type))
  }

  /**
   * @description Check if a File is an image
   * @param file File
   * @returns boolean
   */
  public isImageType(file: File): boolean {
    return file.type ? file.type.startsWith('image/') : this._IsImagePipe.transform(file.name)
  }

  /**
   * @description Prepare payload and Upload APIs
   * @param file File
   * @param index number
   * @param hasConversions boolean
   * @param apiPayload ApiPayload
   * @param uploadProgressList number[]
   * @param useImageCropper boolean
   * @param uploadedImageToCrop File
   * @returns Observable<number | Response<{ uploads: FileResponse }>>[]
   */
  public prepareUploadAPIs(file: File, index: number, apiPayload: ApiPayload = {}, uploadProgressList: number[]): Observable<number | Response<{ uploads: FileResponse }>>[] {
    const observables = [
      this.uploadFile({
        file,
        name: file.name,
        ...apiPayload
      }).pipe(
        tap((res) => {
          if (typeof res === 'number' && res < 100 && res > 0) uploadProgressList[index] = res
        })
      )
    ]
    return observables
  }
}
